// All input is local, untrusted data. Never mutate the system network or infer clinical evidence.
const MAX_NODES = 1000, MAX_EDGES = 5000;
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const fail = message => { throw new Error(message); };
function object(value, allowed, where) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) fail(`${where}必须是对象。`);
    if (Object.keys(value).some(key => !allowed.split(',').includes(key))) fail(`${where}含未支持字段，请使用网络模板，不要包含样本数据。`);
}
function text(value, name, max = 256) {
    if (typeof value !== 'string' || !value.trim() || value.length > max || /[\u0000-\u001f]/.test(value)) fail(`${name}必须是1–${max}字的文本。`);
    return value.trim();
}
function annotation(value, name) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return text(value, name, 4096);
}
export function variantKey(node) {
    const chrom=String(node.chrom).replace(/^chr/i, '').toUpperCase();
    return `${chrom==='MT'?'M':chrom}:${node.pos}:${String(node.ref).toUpperCase()}:${String(node.alt).toUpperCase()}`;
}
const pairKey = (a, b) => JSON.stringify([a, b].sort());
export function normalizeBuild(value) {
    const name = String(value || '').trim().toLowerCase();
    return ['grch37', 'hg19'].includes(name) ? 'GRCh37' : ['grch38', 'hg38'].includes(name) ? 'GRCh38' : null;
}
// Also used for staged Excel rows before the user declares a reference build.
export function normalizeUploadedNode(row, i = 0) {
    object(row, 'id,chrom,pos,ref,alt,group,cDNA,protein,pm3', `位点第${i + 1}行`);
    const id = text(row.id, '位点id', 512), chrom = text(row.chrom, 'chrom', 20).replace(/^chr/i, '').toUpperCase();
    if (!/^(?:[1-9]|1\d|2[0-2]|X|Y|M|MT)$/.test(chrom)) fail(`位点${id}的染色体不支持，仅支持1–22、X、Y、M/MT。`);
    if (!Number.isSafeInteger(row.pos) || row.pos < 1) fail(`位点${id}的pos必须为正整数。`);
    const ref = text(row.ref, 'REF', 1000).toUpperCase(), alt = text(row.alt, 'ALT', 1000).toUpperCase();
    if (!/^[ACGTN]+$/.test(ref) || !/^[ACGTN]+$/.test(alt) || ref === alt) fail(`位点${id}须提供不同的单一REF/ALT碱基序列，不支持符号等位基因或多ALT。`);
    if (ref.length > 1 && alt.length > 1 && (ref[0] === alt[0] || ref.slice(-1) === alt.slice(-1))) fail(`位点${id}仍有共有前后缀，请先标准化。`);
    const node = { id, chrom: chrom === 'MT' ? 'M' : chrom, pos: row.pos, ref, alt, group: row.group == null || row.group === '' ? '' : text(row.group, 'group', 10).toUpperCase(), cDNA: annotation(row.cDNA, 'cDNA'), protein: annotation(row.protein, 'protein'), pm3: annotation(row.pm3, 'PM3') };
    if (node.group && !['P', 'LP', 'V', 'VUS', 'LB', 'B'].includes(node.group)) fail(`位点${id}的group不支持。`);
    return node;
}
export function parseUploadedNetwork(raw, gene, currentDisease) {
    let data;
    try { data = JSON.parse(raw.replace(/^\uFEFF/, '')); } catch { fail('JSON格式不正确，请使用UTF-8网络模板。'); }
    object(data, 'schemaVersion,name,diseaseUnit,gene,genomeBuild,normalized,nodes,edges', '网络文件');
    if (data.schemaVersion !== 1) fail('网络文件schemaVersion必须为1。');
    const name = text(data.name, '网络名称', 100), fileGene = text(data.gene, '基因', 64).toUpperCase();
    if (fileGene !== gene?.toUpperCase()) fail(`上传基因${fileGene}与当前基因${gene || '未选择'}不一致。`);
    const diseaseUnit = data.diseaseUnit == null ? undefined : text(data.diseaseUnit, '疾病单元');
    if (diseaseUnit && currentDisease && diseaseUnit !== currentDisease) fail('上传疾病单元与当前疾病单元不一致。');
    const genomeBuild = normalizeBuild(data.genomeBuild);
    if (!genomeBuild) fail('genomeBuild必须填写GRCh37或GRCh38，不能留空或使用未知版本。');
    if (data.normalized !== true) fail('请先核对变异标准化，并将normalized设为true；系统不自动转换或标准化上传变异。');
    if (!Array.isArray(data.nodes) || !data.nodes.length || data.nodes.length > MAX_NODES) fail(`位点表须包含1–${MAX_NODES}行。`);
    if (!Array.isArray(data.edges) || data.edges.length > MAX_EDGES) fail(`配对表最多${MAX_EDGES}行。`);
    const ids = new Set(), coordinates = new Set();
    const nodes = data.nodes.map((row, i) => {
        const node = normalizeUploadedNode(row, i), id = node.id;
        const key = variantKey(node);
        if (ids.has(id) || coordinates.has(key)) fail(`位点${id}的id或坐标重复，请先去重。`);
        ids.add(id); coordinates.add(key); return node;
    });
    const uniqueEdges = new Map(); let duplicateEdges = 0;
    for (const [i, row] of data.edges.entries()) {
        object(row, 'source,target,weight,genotypeNote', `配对第${i + 1}行`);
        const source = text(row.source, 'source', 512), target = text(row.target, 'target', 512);
        if (!ids.has(source) || !ids.has(target)) fail(`配对第${i + 1}行引用了位点表中不存在的端点。`);
        if (typeof row.weight !== 'number' || !Number.isFinite(row.weight) || row.weight <= 0 || row.weight > 1e9) fail(`配对第${i + 1}行weight须为大于0且不超过10亿的数值。`);
        const key = pairKey(source, target), previous = uniqueEdges.get(key);
        const genotypeNote = row.genotypeNote == null || row.genotypeNote === '' ? undefined : text(row.genotypeNote, '基因型说明');
        if (previous && previous.weight !== row.weight) fail(`配对第${i + 1}行与已有正向/反向配对权重冲突；请先核对，不能相加。`);
        if (previous?.genotypeNote && genotypeNote && previous.genotypeNote !== genotypeNote) fail(`配对第${i + 1}行的基因型来源说明冲突，请先核对。`);
        if (previous) { duplicateEdges++; if (genotypeNote) previous.genotypeNote = genotypeNote; }
        else uniqueEdges.set(key, { source, target, weight: row.weight, ...(genotypeNote ? { genotypeNote } : {}) });
    }
    return { schemaVersion: 1, name, ...(diseaseUnit ? { diseaseUnit } : {}), gene: fileGene, genomeBuild, normalized: true, nodes, edges: [...uniqueEdges.values()], duplicateEdges };
}

export function mergeNetwork(system, upload, confirmedBuild) {
    const knownBuild = normalizeBuild(system.build), declaredBuild = normalizeBuild(confirmedBuild);
    if (!declaredBuild || (knownBuild && knownBuild !== declaredBuild) || upload.genomeBuild !== declaredBuild) fail('参考基因组版本不一致或尚未确认，不能合并。');
    if (system.gene !== upload.gene) fail('当前基因已经变化，请重新上传。');
    if (upload.diseaseUnit && upload.diseaseUnit !== (system.cohort?.name || system.datasetName)) fail('当前疾病单元已经变化，请重新上传。');
    const sourceNodes = system.graphContext?.nodes || system.nodes || [], sourceEdges = system.graphContext?.edges || system.edges || [];
    const info = { sharedNodes: 0, addedNodes: 0, sharedEdges: 0, addedEdges: 0, annotationConflicts: 0, duplicateEdges: upload.duplicateEdges };
    const nodes = sourceNodes.map(node => ({ ...node, mergeOrigin: 'system' }));
    const byCoordinate = new Map(nodes.map(node => [variantKey(node), node])), idMap = new Map();
    for (const row of upload.nodes) {
        const existing = byCoordinate.get(variantKey(row));
        if (existing) {
            existing.mergeOrigin = 'shared'; existing.uploaded = row; idMap.set(row.id, existing.id); info.sharedNodes++;
            if (['group', 'cDNA', 'protein', 'pm3'].some(key => row[key] != null && row[key] !== '' && row[key] !== existing[key])) info.annotationConflicts++;
        } else {
            const node = { ...row, id: `upload:${variantKey(row)}`, label: `${row.chrom}:${row.pos} ${row.ref}>${row.alt}`, mergeOrigin: 'upload', uploaded: row, degree: 0, weightedDegree: 0, gene: upload.gene };
            nodes.push(node); byCoordinate.set(variantKey(row), node); idMap.set(row.id, node.id); info.addedNodes++;
        }
    }
    const edges = sourceEdges.map(edge => ({ ...edge, mergeOrigin: 'system', systemWeight: edge.weight }));
    const byPair = new Map(edges.map(edge => [pairKey(edge.source, edge.target), edge]));
    for (const row of upload.edges) {
        const source = idMap.get(row.source), target = idMap.get(row.target), key = pairKey(source, target), existing = byPair.get(key);
        if (existing) { existing.mergeOrigin = 'shared'; existing.uploadWeight = row.weight; existing.uploadGenotypeNote = row.genotypeNote; info.sharedEdges++; }
        else {
            const edge = { id: `upload-edge:${key}`, source, target, weight: row.weight, uploadWeight: row.weight, uploadGenotypeNote: row.genotypeNote, mergeOrigin: 'upload', homozygous: source === target };
            edges.push(edge); byPair.set(key, edge); info.addedEdges++;
        }
    }
    const byId = new Map(nodes.map(node => [node.id, node]));
    for (const edge of edges) for (const id of new Set([edge.source, edge.target])) {
        const node = byId.get(id);
        if (node?.mergeOrigin === 'upload') { node.degree++; node.weightedDegree += edge.uploadWeight || 0; }
    }
    return { ...system, graphContext: { nodes, edges, targetIds: system.graphContext?.targetIds || [] }, mergeInfo: info, mergeBuild: declaredBuild, mergeName: upload.name };
}

export function networkTemplate(network, gene, build) {
    return { schemaVersion: 1, name: '用户自有', gene, genomeBuild: normalizeBuild(build) || '请填写GRCh37或GRCh38', normalized: false,
        nodes: (network?.nodes || []).map(row => ({ id: row.id, chrom: row.chrom, pos: row.pos, ref: row.ref, alt: row.alt, ...(row.group ? { group: row.group } : {}) })),
        edges: (network?.edges || []).map(row => ({ source: row.source, target: row.target, weight: row.weight })) };
}
